import React, { useState, useEffect } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError } from '../../components';
import css from './FieldSelect.module.css';

// REACT-SELECT DOESN'T PLAY WELL WITH SSR
// IF NO WINDOW, DON'T RENDER
const FieldSelectComponent = props => {
  const [selectedOption, setSelectedOption] = useState(null);
  const {
    rootClassName,
    className,
    selectClassName,
    id,
    label,
    input,
    meta,
    onChange,
    showLabelAsDisabled,
    options = [],
    placeholder = 'Select...',
    isSearchable = false,
    disabled,
    ...rest
  } = props;

  if (typeof window === 'undefined') {
    return null;
  }

  // CONDITIONALLY IMPORT SELECT MODULE
  const { default: Select } = require('react-select'); // eslint-disable-line global-require

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched, error } = meta;
  const hasError = touched && invalid && error;

  // Update selected option when input value changes
  useEffect(() => {
    if (input.value && options) {
      const option = options.find(opt => opt.value === input.value);
      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [input.value, options]);

  const selectClasses = classNames(css.multiSelectBox, {
    [selectClassName]: selectClassName,
    [css.selectError]: hasError,
    [css.selectSuccess]: selectedOption, // Add class for selected option
  });

  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: hasError ? '#FF0000' : '#D8DCE6',
      boxShadow: 'none',
      backgroundColor: '#fff',
      color: '#4a4a4a',
      textAlign: 'left',
      fontSize: "14px",
      '&:hover': {
        borderColor: hasError ? '#FF0000' : '#aaa',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#1A2E4D' : '#fff',
      color: state.isSelected ? '#fff' : '#000',
      fontWeight: 'normal',
      fontSize: "14px",
      '&:hover': {
        backgroundColor: '#1a2e4dde',
        color: '#fff',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#4a4a4a',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 2,
    }),
  };

  const handleChange = (selected) => {
    setSelectedOption(selected);
    input.onChange(selected?.value || '');
    if (onChange) {
      onChange(selected?.value);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id} className={classNames({ [css.labelDisabled]: showLabelAsDisabled })}>
          {label}
        </label>
      ) : null}
      <Select
        styles={customStyles}
        className={selectClasses}
        id={id}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        onBlur={() => input.onBlur(input.value)}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isDisabled={disabled}
        isClearable={false}
        {...rest}
      />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form Field wrapping react-select input
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.selectClassName add more style rules to select component
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id Label is optional, but if it is given, an id is also required so the label can reference the input
 * @param {ReactNode} props.label
 * @param {Array} props.options Array of options in format [{ value: string, label: string }]
 * @param {string} props.placeholder Placeholder text for the select
 * @param {boolean} props.isSearchable Whether the select is searchable
 * @param {boolean} props.disabled Whether the select element is disabled
 * @param {boolean} props.showLabelAsDisabled Whether the label is disabled
 * @returns {JSX.Element} Final Form Field containing react-select input
 */
const FieldSelect = props => {
  return <Field component={FieldSelectComponent} {...props} />;
};

export default FieldSelect;